/*
 * ad-pin-hero.js — per-visitor UTM hero pinning for Shopify collection pages.
 *
 * WHAT IT DOES
 *   A Meta ad promotes a product but links to a COLLECTION page, with the
 *   product handle carried in the URL (default: ?pin=<product-handle>).
 *   On landing, this script pins that product to the top of the collection
 *   grid — but ONLY for the visitor who arrived from the ad. Nothing is written
 *   to Shopify; the global collection order is unchanged for everyone else.
 *
 * WHY IT'S SAFE
 *   - Pure front-end. No backend, no registry, no API tokens. The handle is
 *     auto-detected from the URL the visitor came in on.
 *   - Stock is self-gated using Shopify's public /products/<handle>.js endpoint
 *     (no auth) so a sold-out hero is never featured on paid traffic.
 *   - EVERYTHING runs inside a try/catch. If anything is unexpected (selectors
 *     don't match, fetch fails, product missing) it bails silently and leaves
 *     the page exactly as the theme rendered it. It cannot break the storefront.
 *
 * INSTALL
 *   1. Add this file to your theme assets (e.g. assets/ad-pin-hero.js).
 *   2. Include it on collection pages, e.g. in theme.liquid before </body>:
 *        {{ 'ad-pin-hero.js' | asset_url | script_tag }}
 *      or load it only on collection templates if you prefer.
 *   3. Tune CONFIG below to match your theme's grid markup, then test with
 *        /collections/<any-collection>?pin=<a-product-handle>
 *
 * AD URL CONVENTION
 *   Append ?pin=<product-handle> to the collection URL in the ad's destination.
 *   Keep it OUT of the utm_* namespace so GA4 attribution stays clean. Example:
 *     https://yourstore.com/collections/womens-triangle-bras?pin=organic-triangle-bra&utm_source=facebook&...
 */
(function () {
  'use strict';

  var CONFIG = {
    // URL query param that carries the product handle.
    param: 'pin',

    // Only run on collection pages (path starts with /collections/).
    onlyOnCollections: true,

    // Name of the theme section (sections/<name>.liquid) that renders the
    // theme's real card-product for the hero, fetched via Shopify's Section
    // Rendering API so the injected card looks pixel-identical (swatches, sale
    // badge, compare-at price). Set to '' to skip it and always use the simple
    // built-in placeholder card. See storefront/ad-pin-card.liquid.
    cardSectionId: 'ad-pin-card',

    // TESTING SCOPE — restrict the script to specific collection handles.
    // While you validate the feature, set this to just your test collection so
    // it CANNOT fire anywhere else, e.g. ['ad-pin-test']. Empty = all collections.
    restrictToCollections: [],

    // First selector that matches is treated as the product grid container.
    // Add your theme's grid selector to the front of this list.
    gridSelectors: [
      '[data-ad-pin-grid]',
      '#product-grid',
      '#main-collection-product-grid',
      'ul.product-grid',
      '.collection .product-grid',
      '.collection__products',
    ],

    // Some collections render a non-product marketing tile as the FIRST grid
    // item (e.g. <div class="promo …">). When the grid leads with one of these,
    // the hero is pinned to SECOND position so the promo stays first. Any leading
    // run of these is skipped. Add your theme's promo/banner class here.
    promoSelectors: ['.promo', '[data-ad-pin-skip]'],

    // Stock gate. requireAvailable: don't pin a fully sold-out hero.
    requireAvailable: true,

    // Optional richer gate for alpha-sized apparel: require at least N of the
    // brand's "key sizes" to be available (mirrors the sort tool's stock-health
    // alpha rule, but using only the public availability booleans). Off by
    // default; turn on if you want the underwear key-size logic.
    keySizeMode: false,
    womensKeys: ['XS', 'S', 'M', 'L'],
    mensKeys: ['S', 'M', 'L', 'XL'],
    minKeySizesAvailable: 3,

    // Optional small ribbon added to the pinned card. Empty string = none.
    badgeText: '',

    // data-attribute stamped on the pinned card (for styling / idempotency).
    markerAttr: 'data-ad-pinned',
  };

  // Debug is ON when window.AD_PIN_DEBUG is set OR the URL has ?ad_pin_debug=1.
  // The URL form means you can verify on a phone (where ad traffic lands) with
  // no console — an on-screen banner reports exactly what happened.
  var DEBUG = (function () {
    try {
      if (window.AD_PIN_DEBUG) return true;
      return new URLSearchParams(window.location.search).get('ad_pin_debug') === '1';
    } catch (e) { return false; }
  })();

  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[ad-pin]'].concat([].slice.call(arguments)));
    }
  }

  // On-screen status banner (debug only) — fixed at the bottom so it's visible
  // on mobile. Reports pinned / skipped + reason without needing a console.
  function banner(msg) {
    if (!DEBUG) return;
    try {
      var el = document.getElementById('ad-pin-debug-banner');
      if (!el) {
        el = document.createElement('div');
        el.id = 'ad-pin-debug-banner';
        el.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:99999;background:#111;color:#fff;font:13px/1.4 -apple-system,system-ui,sans-serif;padding:8px 12px;text-align:center';
        (document.body || document.documentElement).appendChild(el);
      }
      el.textContent = '[ad-pin] ' + msg;
    } catch (e) { /* never throw */ }
  }

  // Locale-aware root, e.g. "/" on the primary locale or "/de/" on a localized
  // market (organicbasics.com/de/...). Shopify exposes this in all themes.
  function shopRoot() {
    try {
      var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      if (typeof r === 'string' && r.charAt(0) === '/') {
        return r.charAt(r.length - 1) === '/' ? r : r + '/';
      }
    } catch (e) { /* fall through */ }
    return '/';
  }

  // Pathname with the locale root stripped, always starting with "/".
  // "/de/collections/x" -> "/collections/x". Falls back to a heuristic strip of
  // a leading locale segment if Shopify.routes.root isn't available.
  function localPath() {
    var p = window.location.pathname;
    var root = shopRoot();
    if (root !== '/' && p.toLowerCase().indexOf(root.toLowerCase()) === 0) {
      return '/' + p.slice(root.length);
    }
    var m = p.match(/^\/[a-z]{2}(?:-[a-z]{2,})?(\/(?:collections|products)\/.*)$/i);
    return m ? m[1] : p;
  }

  // Current collection handle from /collections/<handle>[/...]. Null if not on one.
  function currentCollectionHandle() {
    var m = localPath().match(/^\/collections\/([^/?#]+)/);
    return m ? m[1].toLowerCase() : null;
  }

  function getHandleFromUrl() {
    try {
      var value = new URLSearchParams(window.location.search).get(CONFIG.param);
      if (!value) return null;
      // Handles are lowercase alphanumerics + hyphens. Sanitize hard.
      var safe = String(value).trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
      return safe || null;
    } catch (e) {
      return null;
    }
  }

  function onCollectionPage() {
    return /^\/collections\//.test(localPath());
  }

  function findGrid() {
    for (var i = 0; i < CONFIG.gridSelectors.length; i++) {
      var el = document.querySelector(CONFIG.gridSelectors[i]);
      if (el) return el;
    }
    return null;
  }

  // Shopify's public product JSON — no auth, available on every storefront.
  // Fetched under the locale root so it resolves on localized markets (/de/...).
  function fetchProduct(handle) {
    return fetch(shopRoot() + 'products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('product fetch ' + res.status);
      return res.json();
    });
  }

  // Read the size value from a variant's options (option name contains "size").
  function variantSize(product, variant) {
    var names = product.options || [];
    for (var i = 0; i < names.length; i++) {
      var name = (typeof names[i] === 'string' ? names[i] : names[i].name) || '';
      if (/size/i.test(name)) {
        var val = variant.options && variant.options[i];
        if (val) return String(val).trim().toUpperCase();
      }
    }
    return null;
  }

  function passesStockGate(product) {
    if (CONFIG.requireAvailable && !product.available) return false;

    if (CONFIG.keySizeMode) {
      var tags = (product.tags || []).map(function (t) { return String(t).toLowerCase(); });
      var isMens = tags.indexOf('mens') !== -1 || tags.indexOf("men's") !== -1;
      var keys = isMens ? CONFIG.mensKeys : CONFIG.womensKeys;
      var available = {};
      (product.variants || []).forEach(function (v) {
        if (!v.available) return;
        var size = variantSize(product, v);
        if (size && keys.indexOf(size) !== -1) available[size] = true;
      });
      if (Object.keys(available).length < CONFIG.minKeySizesAvailable) return false;
    }

    return true;
  }

  // True if a grid's direct child is a non-product promo/marketing tile — either
  // the child itself matches a promo selector or it wraps one (the .promo class
  // typically sits on an inner node inside the .grid__item wrapper). Bad/unknown
  // selectors are ignored so this can never throw.
  function isPromoCard(node) {
    if (!node || node.nodeType !== 1) return false;
    for (var i = 0; i < CONFIG.promoSelectors.length; i++) {
      var sel = CONFIG.promoSelectors[i];
      try {
        if (node.matches(sel) || node.querySelector(sel)) return true;
      } catch (e) { /* invalid selector — skip */ }
    }
    return false;
  }

  // The node the hero should be inserted BEFORE to sit at the front of the
  // product run. Normally that's the grid's first child, but when the grid leads
  // with one or more promo tiles we skip past them so the hero lands in the first
  // PRODUCT slot (i.e. position 2 behind a single promo). Returns null when the
  // grid is empty or all-promo, which makes insertBefore append after the promos.
  function frontInsertRef(grid) {
    var node = grid.firstElementChild;
    while (node && isPromoCard(node)) node = node.nextElementSibling;
    return node;
  }

  // Find the grid's direct-child card whose product link is an EXACT handle
  // match (not a substring — so "…-black" never matches "…-black-2"). Handles
  // both /products/<h> and /collections/<c>/products/<h> link forms, with or
  // without a locale prefix. `exclude` skips a node (e.g. our own injected card).
  function findCardByHandle(grid, handle, exclude) {
    var links = grid.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var href = links[i].getAttribute('href') || '';
      var m = href.match(/\/products\/([^/?#]+)/);
      if (!m || m[1].toLowerCase() !== handle) continue;

      // Climb to the direct child of the grid (the card wrapper).
      var card = links[i];
      while (card.parentElement && card.parentElement !== grid) card = card.parentElement;
      if (card.parentElement !== grid) continue;
      if (exclude && card === exclude) continue;
      return card;
    }
    return null;
  }

  // If the hero is already rendered in the grid (page 1), move that real card
  // to the front — most faithful to the theme. Returns true if handled.
  function moveExistingCardToFront(grid, handle) {
    var card = findCardByHandle(grid, handle, null);
    if (!card) return false;

    var ref = frontInsertRef(grid);
    if (card !== ref) grid.insertBefore(card, ref);
    card.setAttribute(CONFIG.markerAttr, 'moved');
    addBadge(card);
    log('moved existing card to front', handle);
    return true;
  }

  // Fetch the theme's REAL product card via the Section Rendering API and return
  // it as a ready-to-insert grid item — so the injected card is pixel-identical
  // to every other card (swatches, sale badge, compare-at price). Resolves to
  // null if the section isn't installed or anything fails (caller falls back to
  // the minimal placeholder), so this can never break the page.
  function renderThemeCard(handle) {
    if (!CONFIG.cardSectionId) return Promise.resolve(null);
    var url = shopRoot() + 'products/' + encodeURIComponent(handle) +
      '?section_id=' + encodeURIComponent(CONFIG.cardSectionId);
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) { if (!res.ok) throw new Error('section ' + res.status); return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('.grid__item') ||
          doc.querySelector('[data-ad-pin-card]') ||
          (doc.body && doc.body.firstElementChild);
        if (!node) return null;
        var imported = document.importNode(node, true);
        imported.setAttribute(CONFIG.markerAttr, 'injected-real');
        return imported;
      })
      .catch(function () { return null; });
  }

  // The theme lazy-loads card images via data-src / data-srcset (a load-time
  // script promotes them to src/srcset as cards scroll in). Our injected card
  // arrives after that script ran, so we promote them ourselves — otherwise the
  // image is blank and the hover (secondary) image never loads.
  function promoteLazyImages(root) {
    try {
      var els = root.querySelectorAll('img[data-srcset], img[data-src], source[data-srcset], source[data-src]');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        var dss = el.getAttribute('data-srcset');
        if (dss && !el.getAttribute('srcset')) el.setAttribute('srcset', dss);
        var ds = el.getAttribute('data-src');
        if (ds && !el.getAttribute('src')) el.setAttribute('src', ds);
        if (el.tagName === 'IMG') el.setAttribute('loading', 'eager');
      }
    } catch (e) { /* never throw */ }
  }

  // Minimal built-in placeholder card — used as the instant first paint and as
  // the fallback if renderThemeCard() returns null. Clones the tag/class of the
  // grid's existing first child so it sits in the layout.
  function buildMinimalCard(grid, product, ref) {
    // Clone the tag/class of a real product item (the promo-aware front ref, not
    // a leading promo tile) so the placeholder sits correctly in the layout.
    var sample = ref || grid.firstElementChild;
    var tag = sample ? sample.tagName.toLowerCase() : 'div';
    var card = document.createElement(tag);
    if (sample && sample.className) card.className = sample.className;
    card.setAttribute(CONFIG.markerAttr, 'injected');

    var img = product.featured_image || (product.images && product.images[0]) || '';
    var price = typeof product.price === 'number'
      ? (product.price / 100).toLocaleString(undefined, { style: 'currency', currency: window.Shopify && window.Shopify.currency && window.Shopify.currency.active || 'USD' })
      : '';

    card.innerHTML =
      '<a href="' + shopRoot() + 'products/' + product.handle + '" style="display:block;text-decoration:none;color:inherit">' +
        (img ? '<img src="' + img + '" alt="" loading="eager" style="width:100%;height:auto;display:block">' : '') +
        '<div style="padding:.5rem 0">' +
          '<div>' + escapeHtml(product.title) + '</div>' +
          (price ? '<div>' + price + '</div>' : '') +
        '</div>' +
      '</a>';
    return card;
  }

  // Inject the hero at the top of the grid. Two-phase so there's no blank wait:
  //   1. Paint the minimal placeholder INSTANTLY (its image URL is already known
  //      from the product JSON), so position 1 is filled with zero delay.
  //   2. When the theme's real card finishes rendering, swap it in (with its
  //      lazy images promoted so they show immediately and hover works).
  // Returns the node currently at the front (for endless-scroll dedupe).
  function injectHeroCard(grid, product) {
    var ref = frontInsertRef(grid);
    var placeholder = buildMinimalCard(grid, product, ref);
    grid.insertBefore(placeholder, ref);
    addBadge(placeholder);

    return renderThemeCard(product.handle).then(function (real) {
      if (!real) {
        log('injected card', product.handle, '(placeholder — theme card unavailable)');
        return { card: placeholder, isReal: false };
      }
      promoteLazyImages(real);
      if (placeholder.parentElement) placeholder.parentElement.replaceChild(real, placeholder);
      addBadge(real);
      log('injected card', product.handle, '(theme card)');
      return { card: real, isReal: true };
    }).catch(function () {
      return { card: placeholder, isReal: false };
    });
  }

  // Endless-scroll safety net: after we inject a placeholder for a hero that
  // wasn't on the first page, watch the grid. When the product's REAL card
  // loads in (as the shopper scrolls), remove our placeholder and promote the
  // real, fully-styled card to the front instead — so it can never show twice
  // and ends up looking exactly like every other card.
  function watchForRealCard(grid, handle, placeholder) {
    if (typeof MutationObserver === 'undefined') return;
    var obs = new MutationObserver(function () {
      try {
        var real = findCardByHandle(grid, handle, placeholder);
        if (!real) return;
        obs.disconnect();
        if (placeholder && placeholder.parentElement) placeholder.parentElement.removeChild(placeholder);
        var ref = frontInsertRef(grid);
        if (real !== ref) grid.insertBefore(real, ref);
        promoteLazyImages(real);
        real.setAttribute(CONFIG.markerAttr, 'moved');
        addBadge(real);
        log('endless-scroll: swapped placeholder for real card', handle);
      } catch (e) { obs.disconnect(); }
    });
    obs.observe(grid, { childList: true, subtree: true });
    // Stop watching after 2 minutes regardless, so it can't linger.
    setTimeout(function () { try { obs.disconnect(); } catch (e) {} }, 120000);
  }

  function addBadge(card) {
    if (!CONFIG.badgeText) return;
    if (card.querySelector('[data-ad-pin-badge]')) return;
    var badge = document.createElement('span');
    badge.setAttribute('data-ad-pin-badge', '');
    badge.textContent = CONFIG.badgeText;
    badge.style.cssText = 'position:absolute;top:.5rem;left:.5rem;z-index:2;background:#111;color:#fff;font-size:.7rem;padding:.15rem .4rem;border-radius:2px';
    if (getComputedStyle(card).position === 'static') card.style.position = 'relative';
    card.insertBefore(badge, card.firstChild);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function run() {
    try {
      if (CONFIG.onlyOnCollections && !onCollectionPage()) return;

      // Testing scope: if an allow-list is set, only act on those collections.
      if (CONFIG.restrictToCollections && CONFIG.restrictToCollections.length) {
        var here = currentCollectionHandle();
        if (CONFIG.restrictToCollections.indexOf(here) === -1) {
          log('collection "' + here + '" not in restrictToCollections — skipping');
          return;
        }
      }

      var handle = getHandleFromUrl();
      if (!handle) { log('no ?' + CONFIG.param + ' param — nothing to pin'); return; }

      var grid = findGrid();
      if (!grid) { banner('no product grid found — set CONFIG.gridSelectors'); return; }
      if (grid.querySelector('[' + CONFIG.markerAttr + ']')) return; // idempotent

      fetchProduct(handle)
        .then(function (product) {
          if (!product || !product.handle) { banner('product "' + handle + '" not found'); return; }
          if (!passesStockGate(product)) { banner('"' + handle + '" failed stock gate — not pinned'); return; }
          if (moveExistingCardToFront(grid, product.handle)) {
            banner('pinned "' + handle + '" (moved existing card)');
            return;
          }
          return injectHeroCard(grid, product).then(function (res) {
            watchForRealCard(grid, product.handle, res.card); // dedupe/upgrade on endless scroll
            banner('pinned "' + handle + '" (' + (res.isReal ? 'injected theme card' : 'injected placeholder') + ')');
          });
        })
        .catch(function (err) { banner('skipped: ' + (err && err.message)); });
    } catch (err) {
      // Absolute backstop — never let this break the page.
      log('fatal (ignored):', err && err.message);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();